"use client";

import { type ReactNode, useCallback, useEffect, useState } from "react";
import Image from "next/image";
import { AnimatePresence, motion, useReducedMotion } from "@/lib/motion";
import { FakeCursor, useFakeCursor, type FakeCursorStep } from "@/components/ui";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  Cpu,
  Database,
  Lock,
  MapPin,
  RefreshCw,
  Search,
  Shield,
  ShoppingCart,
  Star,
  TrendingDown,
  TrendingUp,
} from "@/components/icons";
import MarketGlobe from "./market-globe";

/* Polished, real-product mini-demos for the Use Cases bento. Each card shows one
   clean UI moment — a browser frame, a price widget, a rank tracker — floating on
   the card with soft elevation. Most auto-cycle through states on a timer; the SEO
   and Brand cards are instead worked by a fake mouse cursor (see
   @/components/ui/fake-cursor) that clicks their controls to drive the state
   changes. Everything is token-based so the chrome reads correctly in both themes;
   nothing is dark-fixed. Content is pinned to fixed heights so cycling between
   states never resizes a card. Under reduced motion the timers pause, the cursors
   don't render, and each card holds its first state. */

/* Floating surface: white + shadow on the light theme (like real product chrome),
   an elevated darker surface on the dark theme. */
const FLOAT = "bg-popover border-border border shadow-(--shadow-card)";

const GREEN_TINT = "color-mix(in srgb, var(--brand-green) 12%, transparent)";

/** Auto-advance an index through `count` states on a timer. Pauses entirely when
    the user prefers reduced motion, holding on the first state. */
function useAutoCycle(count: number, delay = 3000) {
  const reduced = useReducedMotion();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (reduced || count <= 1) return;
    const t = setInterval(() => setTick((n) => n + 1), delay);
    return () => clearInterval(t);
  }, [reduced, count, delay]);
  return tick % count;
}

/** Soft cross-fade for content that swaps in place (keyed on the changing value).
    Pure opacity so it never nudges layout; an optional stagger delay lets a row
    list cascade in instead of popping all at once. */
function fadeKey(reduced: boolean | null, delay = 0) {
  return reduced
    ? {}
    : {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { duration: 0.4, delay },
      };
}

/* ---- shared chrome ---- */

interface DropdownProps {
  children: ReactNode;
  icon?: ReactNode;
  className?: string;
  /** Rotates the chevron while the fake menu is open. */
  open?: boolean;
}
/** A read-only dropdown pill — the label swaps on its own; the chevron is decor. */
function Dropdown({ children, icon, className, open = false }: DropdownProps) {
  return (
    <span
      className={cn(
        "text-foreground inline-flex items-center gap-2 rounded-lg px-3 py-2 text-[13px] font-medium",
        FLOAT,
        className
      )}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      <ChevronDown
        size={14}
        className={cn(
          "text-muted-foreground shrink-0 transition-transform duration-200",
          open && "rotate-180"
        )}
        aria-hidden="true"
      />
    </span>
  );
}

/** A single mini-browser window: window controls, back/forward/reload, an address
    bar (padlock + domain + bookmark) and the proxy IP the request exits through, over
    the page it rendered. Stacked into a deck by the scraping visual; the page runs
    longer than the frame and clips at the fold. */
function BrowserWindow({
  domain,
  ip,
  children,
}: {
  domain: string;
  ip: string;
  children: ReactNode;
}) {
  return (
    <div className="border-border bg-popover flex h-full flex-col overflow-hidden rounded-xl border shadow-lg">
      {/* Chrome — window controls + nav + address bar + exit IP */}
      <div className="border-border flex shrink-0 items-center gap-2 border-b px-3 py-2">
        <span className="flex shrink-0 items-center gap-1.5" aria-hidden="true">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "#ff5f57" }} />
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "#febc2e" }} />
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "#28c840" }} />
        </span>
        <span
          className="text-muted-foreground hidden shrink-0 items-center gap-1 sm:flex"
          aria-hidden="true"
        >
          <ArrowLeft size={13} />
          <ArrowRight size={13} className="opacity-40" />
          <RefreshCw size={11} />
        </span>
        <span className="bg-foreground/5 flex h-6 min-w-0 flex-1 items-center gap-2 rounded-full px-2.5 text-[11px]">
          <Lock size={11} className="text-brand-green shrink-0" aria-hidden="true" />
          <span className="text-muted-foreground min-w-0 flex-1 truncate">{domain}</span>
          <Star size={11} className="text-muted-foreground/50 shrink-0" aria-hidden="true" />
        </span>
        <span
          className="flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium tabular-nums"
          style={{ backgroundColor: GREEN_TINT, color: "var(--brand-green)" }}
        >
          <span className="bg-brand-green h-1.5 w-1.5 rounded-full" aria-hidden="true" />
          {ip}
        </span>
      </div>
      {/* Page */}
      <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
    </div>
  );
}

/* ============================ WEB SCRAPING ============================ */
/* "Scrape any website" — three sites, each in its own browser window, stacked like a
   deck. The front window is the page being read; the others peek behind with a
   shadow and a slight offset, and the deck shuffles every few seconds. Each window
   fetches through its own proxy IP; nothing is tied to a country or flag. Store
   photos are the real shots in public/use-cases. */
const STORE_PRODUCTS = [
  {
    name: "Everyday Backpack",
    price: "$74.00",
    image: "/use-cases/store-backpack.jpg",
    alt: "Gray canvas everyday backpack with a water bottle in the side pocket",
  },
  {
    name: "Minimalist Watch",
    price: "$149.00",
    image: "/use-cases/store-watch.jpg",
    alt: "Minimalist analog wristwatch with a leather strap",
  },
  {
    name: "LED Desk Lamp",
    price: "$39.00",
    image: "/use-cases/store-lamp.jpg",
    alt: "Adjustable LED desk lamp on a stone base",
  },
  {
    name: "Insulated Bottle",
    price: "$29.00",
    image: "/use-cases/store-bottle.png",
    alt: "Stainless-steel insulated water bottle",
  },
];

const BLOG_POSTS = [
  { title: "Field notes from a week off the grid", meta: "Travel · 6 min" },
  { title: "The tools on my desk in 2026", meta: "Gear · 4 min" },
  { title: "A slower approach to morning routines", meta: "Life · 5 min" },
];

const ESTATE_LISTINGS = [
  {
    image: "/use-cases/estate-1.jpg",
    alt: "Modern two-storey home with a landscaped front yard",
    price: "$540,000",
    specs: "3 bd · 2 ba · 1,450 sqft",
    place: "Maple Heights",
  },
  {
    image: "/use-cases/estate-2.jpg",
    alt: "Contemporary house with large glass windows",
    price: "$720,000",
    specs: "4 bd · 3 ba · 2,100 sqft",
    place: "Riverside",
  },
  {
    image: "/use-cases/estate-3.jpg",
    alt: "Brick bungalow with a covered porch",
    price: "$395,000",
    specs: "2 bd · 1 ba · 980 sqft",
    place: "Old Town",
  },
  {
    image: "/use-cases/estate-4.jpg",
    alt: "Lakeside home with a wooden deck",
    price: "$610,000",
    specs: "3 bd · 2 ba · 1,600 sqft",
    place: "Lakeview",
  },
];

const SITES = [
  { id: "shop", domain: "shopwave.com", ip: "104.28.42.16" },
  { id: "blog", domain: "thejournal.blog", ip: "45.83.118.77" },
  { id: "estate", domain: "nestly.homes", ip: "188.42.90.135" },
];

const STORE_HERO_BG =
  "linear-gradient(115deg, color-mix(in srgb, var(--brand-green) 26%, var(--card)), var(--card))";

/* --- the sites the crawler visits; each is a full page (its own site nav + content)
   that runs longer than the viewport, so it reads like a real page continuing below
   the fold rather than a short card floating in empty space. --- */

function StoreSite() {
  return (
    <div>
      {/* the site's own top nav */}
      <div className="border-border flex items-center gap-2 border-b px-3.5 py-2">
        <span className="bg-brand-green h-5 w-5 rounded-md" aria-hidden="true" />
        <span className="text-foreground text-[12px] font-semibold tracking-tight">ShopWave</span>
        <span className="text-muted-foreground ml-3 hidden gap-3 text-[11px] sm:flex">
          <span>Deals</span>
          <span>New</span>
          <span>Brands</span>
        </span>
        <span
          className="text-muted-foreground ml-auto flex items-center gap-2.5"
          aria-hidden="true"
        >
          <Search size={13} />
          <ShoppingCart size={13} />
        </span>
      </div>
      <div className="space-y-3 p-3.5">
        <div
          className="flex items-center justify-between gap-3 overflow-hidden rounded-lg px-4 py-4"
          style={{ background: STORE_HERO_BG }}
        >
          <div className="min-w-0">
            <p className="text-foreground text-[14px] font-semibold">Summer Sale</p>
            <p className="text-muted-foreground text-[11px]">Up to 40% off top brands</p>
          </div>
          <span className="bg-primary text-primary-foreground shrink-0 rounded-full px-3 py-1 text-[11px] font-medium">
            Shop now
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {["All", "Audio", "Footwear", "Home"].map((c, i) => (
            <span
              key={c}
              className={cn(
                "rounded-full px-2.5 py-1 text-[10.5px] font-medium",
                i === 0
                  ? "bg-foreground/10 text-foreground"
                  : "border-border text-muted-foreground border"
              )}
            >
              {c}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <p className="text-foreground text-[12px] font-semibold">Popular right now</p>
          <span className="text-brand-green text-[11px] font-medium">View all</span>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {STORE_PRODUCTS.map((p) => (
            <div key={p.name} className="min-w-0">
              <div className="bg-foreground/5 relative aspect-4/3 overflow-hidden rounded-md">
                <Image src={p.image} alt={p.alt} fill sizes="180px" className="object-cover" />
              </div>
              <p className="text-foreground mt-1.5 truncate text-[10.5px] font-medium">{p.name}</p>
              <p className="text-foreground text-[10.5px] font-semibold">{p.price}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BlogSite() {
  return (
    <div>
      {/* masthead */}
      <div className="border-border flex items-center gap-2 border-b px-3.5 py-2">
        <span className="text-foreground text-[13px] font-bold tracking-tight">The Journal</span>
        <span className="text-muted-foreground ml-3 hidden gap-3 text-[11px] sm:flex">
          <span>Latest</span>
          <span>Design</span>
          <span>Travel</span>
        </span>
        <Search size={13} className="text-muted-foreground ml-auto" aria-hidden="true" />
      </div>
      <div className="space-y-3 p-3.5">
        {/* featured post with hero image */}
        <div>
          <div className="bg-foreground/5 relative h-36 overflow-hidden rounded-lg">
            <Image
              src="/use-cases/blog-hero.jpg"
              alt="Sunlit workspace with a laptop, notebook, and coffee"
              fill
              sizes="480px"
              className="object-cover"
            />
          </div>
          <p className="text-brand-green mt-2 text-[10px] font-semibold tracking-wide uppercase">
            Design · 5 min read
          </p>
          <p className="text-foreground text-[15px] leading-snug font-semibold">
            How we rebuilt our design system from scratch
          </p>
          <p className="text-muted-foreground mt-1 line-clamp-2 text-[11px] leading-relaxed">
            A look at the tokens, components, and quiet decisions that made the redesign feel
            effortless — and what we&apos;d do differently next time.
          </p>
        </div>
        {/* recent posts */}
        <div className="divide-border border-border divide-y border-t">
          {BLOG_POSTS.map((p) => (
            <div key={p.title} className="py-2">
              <p className="text-foreground truncate text-[11.5px] font-medium">{p.title}</p>
              <p className="text-muted-foreground text-[10.5px]">{p.meta}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EstateSite() {
  return (
    <div>
      {/* site header */}
      <div className="border-border flex items-center gap-2 border-b px-3.5 py-2">
        <span className="bg-brand-green h-5 w-5 rounded-md" aria-hidden="true" />
        <span className="text-foreground text-[12px] font-semibold tracking-tight">Nestly</span>
        <span className="text-muted-foreground ml-3 hidden gap-3 text-[11px] sm:flex">
          <span>Buy</span>
          <span>Rent</span>
          <span>Sell</span>
        </span>
        <Search size={13} className="text-muted-foreground ml-auto" aria-hidden="true" />
      </div>
      <div className="p-3.5">
        <div className="mb-1.5 flex items-center justify-between text-[12px]">
          <span className="text-foreground font-semibold">Homes for sale</span>
          <span className="text-muted-foreground text-[10.5px]">1,204 listings</span>
        </div>
        <div className="divide-border divide-y">
          {ESTATE_LISTINGS.map((l) => (
            <div key={l.image} className="flex items-center gap-3 py-2">
              <div className="bg-foreground/5 relative h-14 w-20 shrink-0 overflow-hidden rounded-md">
                <Image src={l.image} alt={l.alt} fill sizes="88px" className="object-cover" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-foreground text-[12px] font-semibold">{l.price}</p>
                <p className="text-muted-foreground truncate text-[10.5px]">{l.specs}</p>
                <p className="text-muted-foreground mt-0.5 flex items-center gap-1 truncate text-[10px]">
                  <MapPin size={9} className="shrink-0" aria-hidden="true" />
                  {l.place}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function siteContent(id: string) {
  if (id === "shop") return <StoreSite />;
  if (id === "blog") return <BlogSite />;
  return <EstateSite />;
}

/** Web scraping — three sites, each in its own browser window, stacked like a deck.
    The front window is the site currently being read; the others peek behind it with
    a shadow and a slight upward offset. Every few seconds the deck shuffles — the
    front window drops to the back and the next rises — cycling a store, a news site,
    and a reviews site, each fetched through its own proxy IP. No countries, no flags. */
export function ScrapingVisual() {
  const reduced = useReducedMotion();
  const [front, setFront] = useState(0);
  const N = SITES.length;

  useEffect(() => {
    if (reduced) return;
    const t = setInterval(() => setFront((f) => (f + 1) % N), 3400);
    return () => clearInterval(t);
  }, [reduced, N]);

  return (
    <div className="mt-5 flex min-h-0 flex-1 flex-col gap-4">
      {/* Stacked browser windows — front one active, the rest peek behind */}
      <div className="relative min-h-80 flex-1">
        {SITES.map((s, i) => {
          const depth = (i - front + N) % N; // 0 = front
          return (
            <motion.div
              key={s.id}
              className="absolute inset-x-0 top-24 bottom-0"
              style={{ zIndex: N - depth }}
              initial={false}
              animate={{
                x: depth * 26,
                y: -depth * 38,
                scale: 1 - depth * 0.05,
              }}
              transition={reduced ? { duration: 0 } : { duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <BrowserWindow domain={s.domain} ip={s.ip}>
                {siteContent(s.id)}
              </BrowserWindow>
            </motion.div>
          );
        })}
      </div>

      {/* Live status */}
      <div className="flex shrink-0 items-center gap-2 text-[12px]">
        <span className="text-brand-green flex items-center gap-1.5 font-medium">
          <span className="relative flex h-2 w-2" aria-hidden="true">
            {!reduced && (
              <span className="bg-brand-green absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" />
            )}
            <span className="bg-brand-green relative inline-flex h-2 w-2 rounded-full" />
          </span>
          200 OK
        </span>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground">mobile exit · rotating session</span>
      </div>
    </div>
  );
}

/* ============================ PRICE MONITORING ============================ */
interface PriceProduct {
  name: string;
  price: string;
  delta: string;
  down: boolean;
  stores: number;
  hue: string;
  spark: number[];
  /** Real product photo served from public/ (square crops cleanest). When unset,
      the card falls back to the ShoppingCart icon. */
  image?: string;
  imageAlt?: string;
}

const PRICE_PRODUCTS: PriceProduct[] = [
  {
    name: "Wireless Headphones",
    price: "$119.00",
    delta: "-7.8%",
    down: true,
    stores: 3,
    hue: "var(--brand-green)",
    spark: [30, 34, 31, 36, 32, 27, 22, 18],
    image: "/use-cases/headphones.png",
    imageAlt: "Black over-ear wireless headphones",
  },
  {
    name: "Running Sneakers",
    price: "$89.95",
    delta: "-12.0%",
    down: true,
    stores: 5,
    hue: "var(--brand-green)",
    spark: [34, 31, 33, 28, 30, 24, 21, 16],
    image: "/use-cases/running-sneakers.png",
    imageAlt: "Gray running sneaker with lime-green accents",
  },
  {
    name: "Coffee Maker",
    price: "$62.49",
    delta: "+3.4%",
    down: false,
    stores: 4,
    hue: "var(--destructive)",
    spark: [18, 22, 20, 25, 23, 29, 27, 32],
    image: "/use-cases/coffee-maker.png",
    imageAlt: "Stainless-steel espresso machine with a built-in bean grinder",
  },
];

interface ProductThumbProps {
  src?: string;
  alt?: string;
  hue: string;
}

/* Product thumbnail for the price tracker — shows the real product photo when one
   is set, otherwise a neutral ShoppingCart placeholder so the slot still reads as
   "product image here". Keyed by product in the parent, so the on-error fallback
   resets on each cycle. */
function ProductThumb({ src, alt, hue }: ProductThumbProps) {
  const [failed, setFailed] = useState(false);
  const showImage = !!src && !failed;

  return (
    <span
      className={cn(
        "flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl",
        showImage && "border-border bg-background border"
      )}
      style={
        showImage ? undefined : { backgroundColor: `color-mix(in srgb, ${hue} 14%, transparent)` }
      }
    >
      {src && !failed ? (
        <Image
          src={src}
          alt={alt ?? ""}
          width={64}
          height={64}
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <ShoppingCart size={24} style={{ color: hue }} aria-hidden="true" />
      )}
    </span>
  );
}

/** Price monitoring — a clean tracker widget: product, live price + trend, and a
    sparkline. Auto-cycles the tracked product. */
export function PriceVisual() {
  const reduced = useReducedMotion();
  const idx = useAutoCycle(PRICE_PRODUCTS.length, 3000);
  const p = PRICE_PRODUCTS[idx];

  const W = 240;
  const H = 52;
  const max = Math.max(...p.spark);
  const min = Math.min(...p.spark);
  const step = W / (p.spark.length - 1);
  const norm = (v: number) => H - 5 - ((v - min) / (max - min || 1)) * (H - 12);
  const line = p.spark
    .map((v, i) => `${i === 0 ? "M" : "L"} ${(i * step).toFixed(1)} ${norm(v).toFixed(1)}`)
    .join(" ");
  const area = `${line} L ${W} ${H} L 0 ${H} Z`;

  return (
    <div className="mt-5">
      <div className={cn("rounded-xl p-4", FLOAT)}>
        <div className="flex items-center gap-3">
          <ProductThumb key={p.name} src={p.image} alt={p.imageAlt} hue={p.hue} />
          <div className="min-w-0 flex-1">
            <motion.p
              key={p.name}
              {...fadeKey(reduced)}
              className="text-foreground truncate text-[13px] font-medium"
            >
              {p.name}
            </motion.p>
            <p className="text-muted-foreground text-[11.5px]">{p.stores} retailers tracked</p>
          </div>
          <div className="text-right">
            <motion.p
              key={p.price}
              {...fadeKey(reduced)}
              className="text-foreground text-[15px] font-semibold tracking-tight"
            >
              {p.price}
            </motion.p>
            <span
              className={cn(
                "flex items-center justify-end gap-0.5 text-[11.5px] font-medium",
                p.down ? "text-brand-green" : "text-destructive"
              )}
            >
              {p.down ? (
                <TrendingDown size={11} aria-hidden="true" />
              ) : (
                <TrendingUp size={11} aria-hidden="true" />
              )}
              {p.delta}
            </span>
          </div>
        </div>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="mt-4 h-13 w-full"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id={`uc-spark-${idx}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={p.hue} stopOpacity="0.18" />
              <stop offset="100%" stopColor={p.hue} stopOpacity="0" />
            </linearGradient>
          </defs>
          <motion.path
            key={`area-${idx}`}
            d={area}
            fill={`url(#uc-spark-${idx})`}
            initial={reduced ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          />
          <motion.path
            key={`line-${idx}`}
            d={line}
            fill="none"
            stroke={p.hue}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            initial={reduced ? false : { pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          />
        </svg>
      </div>
    </div>
  );
}

/* ============================ SEO MONITORING ============================ */
/* A two-result SERP snippet the way a rank tracker sees it. We monitor one listing
   across a set of target keywords; its rank shifts per query — #1 for some, #2 for
   others — so as the keyword cycles the tracked card animates between the top and
   second slot while a keyword-specific competitor fills the other. Both results swap
   their content to match the query. The tracked card carries a green tint so it's
   clear which result is being watched. Unbranded throughout. */

/* Per keyword: the search query, the tracked listing's rank for it, the tracked
   listing's own content, and the keyword-specific competitor in the other slot. */
const SEO_KEYWORDS = [
  {
    query: "mobile proxies",
    rank: 1,
    tracked: {
      title: "Mobile Proxy Network — Global 4G & 5G IPs",
      description:
        "Premium pooled mobile IPs worldwide, with automatic rotation and precise geo-targeting for large-scale automation.",
    },
    competitor: {
      title: "Best Mobile Proxy Providers, Ranked (2026)",
      description:
        "Compare mobile IPs across major networks for reliable local mobile data collection at scale.",
    },
  },
  {
    query: "residential proxies",
    rank: 2,
    tracked: {
      title: "Residential Proxy Pool — 100M+ Ethical IPs",
      description:
        "Sticky or rotating residential IPs sourced from real devices, with city-level targeting and 99.9% uptime.",
    },
    competitor: {
      title: "Residential Proxies Explained — 2026 Guide",
      description:
        "How residential IP networks work, when to use them, and what to look for when choosing a provider.",
    },
  },
  {
    query: "rotating proxy API",
    rank: 1,
    tracked: {
      title: "Rotating Proxy API — One Endpoint, Millions of IPs",
      description:
        "A single endpoint that rotates the exit IP on every request, with sticky sessions and automatic retries built in.",
    },
    competitor: {
      title: "How to Build a Rotating Proxy in Python",
      description:
        "A step-by-step tutorial for cycling IP addresses across requests using open-source libraries.",
    },
  },
];

/* A green-tinted popover surface for the tracked card — flips with the theme. */
const TRACKED_TINT = "color-mix(in srgb, var(--brand-green) 12%, var(--popover))";

/* Click the search bar to run the next tracked keyword, then drift down to the
   tracked (green) result to check its rank — following it if it changed slots. */
const SEO_CURSOR_SCRIPT: FakeCursorStep[] = [
  { id: "next-keyword", target: "seo-search", at: 0.3, hold: 2250 },
  { id: "check-rank", target: "seo-tracked", at: 0.85, atY: 0.3, hold: 1000, arrive: "release" },
];

/** SEO monitoring — a live rank tracker. The fake cursor clicks the search bar to
    run each keyword; the tracked listing (green-tinted) holds #1 for some keywords
    and slips to #2 for others, animating between slots, with a keyword-specific
    competitor in the other. Both results swap their content per query. Unbranded. */
export function SeoVisual() {
  const reduced = useReducedMotion();
  const [idx, setIdx] = useState(0);
  const kw = SEO_KEYWORDS[idx];

  const handleClick = useCallback((s: FakeCursorStep) => {
    if (s.id === "next-keyword") setIdx((v) => (v + 1) % SEO_KEYWORDS.length);
  }, []);

  const { frameRef, setTarget, cursor, clicking } = useFakeCursor({
    script: SEO_CURSOR_SCRIPT,
    onClick: handleClick,
  });

  const tracked = { id: "tracked", tracked: true, ...kw.tracked };
  const competitor = { id: "competitor", tracked: false, ...kw.competitor };
  const ordered = kw.rank === 1 ? [tracked, competitor] : [competitor, tracked];

  return (
    <div ref={frameRef} className="relative mt-5 space-y-3">
      <FakeCursor cursor={cursor} clicking={clicking} />
      {/* Search bar — the tracked keyword cycles in place */}
      <div className="flex items-center gap-2">
        <span
          ref={setTarget("seo-search")}
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2 rounded-full px-3.5 py-2 text-[13px]",
            FLOAT
          )}
        >
          <Search size={13} className="text-muted-foreground shrink-0" aria-hidden="true" />
          <motion.span key={kw.query} {...fadeKey(reduced)} className="text-foreground truncate">
            {kw.query}
          </motion.span>
        </span>
      </div>

      {/* Two results. The tracked card (green-tinted) animates between slots as its
          rank changes; the keyword-specific competitor fills the other. Both cards
          fade their content to the new query. Titles are single-line; descriptions
          clamp to two, so heights stay fixed and the reorder reads as a clean swap. */}
      <div className="flex flex-col gap-2">
        {ordered.map((item) => (
          <motion.div
            key={item.id}
            ref={item.tracked ? setTarget("seo-tracked") : undefined}
            layout={!reduced}
            transition={reduced ? { duration: 0 } : { duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className={cn(
              "relative rounded-xl px-3.5 py-3",
              item.tracked ? "border-brand-green/40 border shadow-(--shadow-card)" : FLOAT
            )}
            style={item.tracked ? { backgroundColor: TRACKED_TINT } : undefined}
          >
            {item.tracked && (
              <motion.span
                key={`${kw.query}-rank`}
                {...fadeKey(reduced)}
                className="absolute top-3 right-3 rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold"
                style={{
                  backgroundColor: "color-mix(in srgb, var(--brand-green) 16%, transparent)",
                  color: "var(--brand-green)",
                }}
              >
                #{kw.rank}
              </motion.span>
            )}
            <motion.p
              key={`${kw.query}-${item.id}-t`}
              {...fadeKey(reduced)}
              className={cn(
                "light:text-[#1a0dab] truncate text-[14px] font-medium text-[#8ab4f8]",
                item.tracked && "pr-10"
              )}
            >
              {item.title}
            </motion.p>
            <motion.p
              key={`${kw.query}-${item.id}-d`}
              {...fadeKey(reduced)}
              className="text-muted-foreground mt-1 line-clamp-2 text-[11.5px] leading-relaxed"
            >
              {item.description}
            </motion.p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ============================ AI & LLM TRAINING ============================ */
/* The data mixture of a training corpus — the composition an LLM team reviews,
   broken down by the kind of web source the crawler pulled (news, reviews,
   docs, forums, code). Proportions fill in as bars; the highlighted source
   cycles so the card reads as a live corpus, not a static file. pct sums to 100. */
const AI_SOURCES = [
  { id: "articles", label: "News & articles", pct: 34 },
  { id: "reviews", label: "Product reviews", pct: 24 },
  { id: "docs", label: "Docs & wikis", pct: 18 },
  { id: "forums", label: "Forums & Q&A", pct: 14 },
  { id: "code", label: "Code & repos", pct: 10 },
];

/** AI & LLM training — a training-corpus composition panel. A source breakdown
    fills in as proportion bars, with the highlighted source auto-cycling. Reads
    as the data mixture behind a model; token-based for both themes. */
export function AiVisual() {
  const reduced = useReducedMotion();
  const active = useAutoCycle(AI_SOURCES.length, 2000);

  return (
    <div className="mt-5 space-y-3">
      {/* Corpus header */}
      <div className={cn("flex items-center gap-3 rounded-xl p-3.5", FLOAT)}>
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: GREEN_TINT }}
        >
          <Database size={18} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-foreground text-[13px] font-medium">Training corpus</p>
          <p className="text-muted-foreground text-[11.5px]">2.4M tokens · 5 sources</p>
        </div>
        <span
          className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium"
          style={{ backgroundColor: GREEN_TINT, color: "var(--brand-green)" }}
        >
          <Check size={11} aria-hidden="true" /> Balanced
        </span>
      </div>

      {/* Source composition — proportion bars, active source highlighted */}
      <div className={cn("space-y-2.5 rounded-xl p-3.5", FLOAT)}>
        {AI_SOURCES.map((s, i) => {
          const on = i === active;
          return (
            <div key={s.id} className="flex items-center gap-3">
              <span
                className={cn(
                  "w-24 shrink-0 truncate text-[11px] transition-colors duration-300",
                  on ? "text-foreground font-medium" : "text-muted-foreground"
                )}
              >
                {s.label}
              </span>
              <span className="bg-foreground/8 relative h-1.5 flex-1 overflow-hidden rounded-full">
                <motion.span
                  className="absolute inset-y-0 left-0 rounded-full transition-colors duration-300"
                  style={{
                    backgroundColor: on
                      ? "var(--brand-green)"
                      : "color-mix(in srgb, var(--brand-green) 40%, transparent)",
                  }}
                  initial={reduced ? false : { width: 0 }}
                  animate={{ width: `${s.pct}%` }}
                  transition={{ duration: 0.6, delay: reduced ? 0 : i * 0.08, ease: "easeOut" }}
                />
              </span>
              <span
                className={cn(
                  "w-8 shrink-0 text-right text-[11px] tabular-nums transition-colors duration-300",
                  on ? "text-brand-green font-medium" : "text-muted-foreground"
                )}
              >
                {s.pct}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================ MARKET RESEARCH ============================ */
/** Market research — an auto-spinning particle globe (see market-globe.tsx),
    cropped low and bled to the card edges so it reads as a soft backdrop that
    evokes reach across every geography. */
export function MarketVisual() {
  return (
    <div className="relative -mx-6 mt-5 -mb-6 sm:-mx-7 sm:-mb-7">
      <MarketGlobe />
    </div>
  );
}

/* ============================ BRAND PROTECTION ============================ */
/* A marketplace enforcement monitor. Each scan surfaces the violation types an
   anti-counterfeiting team actually chases — counterfeits, trademark and MAP
   breaches, grey-market imports, unauthorized sellers — each with the live
   enforcement status (takedown filed, notice sent, verified). Marketplaces are
   the real counterfeiting hotspots; scanned/violation counts update per scan. */
const BRAND_MARKETS = [
  {
    name: "Amazon",
    scanned: "48,210",
    violations: "37",
    alerts: [
      { label: "Counterfeit listing detected", tone: "red", status: "Takedown filed" },
      { label: "Unauthorized 3P seller", tone: "amber", status: "Notice sent" },
      { label: "Authorized reseller", tone: "green", status: "Verified" },
    ],
  },
  {
    name: "AliExpress",
    scanned: "91,430",
    violations: "128",
    alerts: [
      { label: "Trademark infringement", tone: "red", status: "Reported" },
      { label: "Logo misuse on listing", tone: "amber", status: "Under review" },
      { label: "Official brand store", tone: "green", status: "Verified" },
    ],
  },
  {
    name: "eBay",
    scanned: "22,870",
    violations: "19",
    alerts: [
      { label: "MAP price violation", tone: "amber", status: "Notice sent" },
      { label: "Grey-market import", tone: "red", status: "Takedown filed" },
      { label: "Official outlet", tone: "green", status: "Verified" },
    ],
  },
] as const;

const TONE_DOT: Record<string, string> = {
  red: "bg-red-400 light:bg-red-600",
  amber: "bg-amber-400 light:bg-amber-600",
  green: "bg-brand-green",
};

/* Enforcement-status pill styling per severity. */
const TONE_CHIP: Record<string, string> = {
  red: "bg-red-400/15 text-red-500 light:text-red-600",
  amber: "bg-amber-400/15 text-amber-500 light:text-amber-700",
  green: "bg-brand-green/12 text-brand-green",
};

/* Click the marketplace dropdown to open its menu, glide down to the next
   market and select it, then drift down the refreshed violation feed. */
const BRAND_CURSOR_SCRIPT: FakeCursorStep[] = [
  { id: "open-market", target: "brand-market", hold: 2000 },
  { id: "pick-market", target: "brand-option", hold: 650 },
  { id: "scan-alerts", target: "brand-alerts", at: 0.85, atY: 0.4, hold: 950, arrive: "release" },
];

/** Brand protection — a live marketplace enforcement monitor: a scan counter,
    violation feed with severity + enforcement status, and a marketplace dropdown.
    The fake cursor opens the dropdown, picks the next marketplace from its menu,
    and the feed refreshes for that scan. */
export function BrandVisual() {
  const reduced = useReducedMotion();
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const market = BRAND_MARKETS[idx];
  const nextIdx = (idx + 1) % BRAND_MARKETS.length;

  const handleClick = useCallback((s: FakeCursorStep) => {
    if (s.id === "open-market") setOpen(true);
    if (s.id === "pick-market") {
      setOpen(false);
      setIdx((v) => (v + 1) % BRAND_MARKETS.length);
    }
  }, []);

  const { frameRef, setTarget, cursor, clicking } = useFakeCursor({
    script: BRAND_CURSOR_SCRIPT,
    onClick: handleClick,
  });

  return (
    <div ref={frameRef} className="relative mt-5 space-y-3">
      <FakeCursor cursor={cursor} clicking={clicking} />
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground flex items-center gap-1.5 text-[12px] font-medium">
            <span className="relative flex h-2 w-2" aria-hidden="true">
              {!reduced && (
                <span className="bg-brand-green absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" />
              )}
              <span className="bg-brand-green relative inline-flex h-2 w-2 rounded-full" />
            </span>
            Live scan
          </span>
          <span ref={setTarget("brand-market")} className="relative shrink-0">
            <Dropdown
              className="w-36 py-1.5"
              open={open}
              icon={<Shield size={13} style={{ color: "var(--brand-green)" }} aria-hidden="true" />}
            >
              <motion.span key={market.name} {...fadeKey(reduced)}>
                {market.name}
              </motion.span>
            </Dropdown>
            {/* The menu the cursor opens — the option it will pick carries the
                "brand-option" target; the current market shows a check. */}
            <AnimatePresence>
              {open && (
                <motion.span
                  initial={{ opacity: 0, y: -4, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.98 }}
                  transition={{ duration: 0.18 }}
                  className={cn(
                    "absolute top-full right-0 z-10 mt-1 block w-36 rounded-lg p-1",
                    FLOAT
                  )}
                >
                  {BRAND_MARKETS.map((m, i) => (
                    <span
                      key={m.name}
                      ref={i === nextIdx ? setTarget("brand-option") : undefined}
                      className={cn(
                        "flex items-center justify-between rounded-md px-2 py-1.5 text-[12px]",
                        m.name === market.name
                          ? "text-foreground bg-foreground/5 font-medium"
                          : "text-muted-foreground"
                      )}
                    >
                      {m.name}
                      {m.name === market.name && (
                        <Check
                          size={11}
                          style={{ color: "var(--brand-green)" }}
                          aria-hidden="true"
                        />
                      )}
                    </span>
                  ))}
                </motion.span>
              )}
            </AnimatePresence>
          </span>
        </div>
        <motion.p
          key={market.name}
          {...fadeKey(reduced)}
          className="text-muted-foreground text-[11.5px] tabular-nums"
        >
          <span className="text-foreground font-semibold">{market.scanned}</span> listings scanned ·{" "}
          <span className="light:text-amber-700 font-semibold text-amber-500">
            {market.violations}
          </span>{" "}
          violations
        </motion.p>
      </div>
      <div ref={setTarget("brand-alerts")} className="space-y-2">
        {market.alerts.map((alert, i) => (
          <motion.div
            key={`${market.name}-${alert.label}`}
            {...fadeKey(reduced, i * 0.09)}
            className={cn("flex items-center gap-2.5 rounded-lg px-3 py-2.5", FLOAT)}
          >
            <span className={cn("h-2 w-2 shrink-0 rounded-full", TONE_DOT[alert.tone])} />
            <span className="text-foreground min-w-0 flex-1 truncate text-[12px]">
              {alert.label}
            </span>
            <span
              className={cn(
                "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium",
                TONE_CHIP[alert.tone]
              )}
            >
              {alert.status}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* =========================== SECURITY TESTING =========================== */
/** Cybersecurity testing — an authorized assessment running through its
    probes from a real mobile exit, each check ticking green as the scan
    advances, capped by a passed badge. */

const SECURITY_PROBES = [
  { id: "sp1", label: "Network origin", detail: "Mobile ISP exit" },
  { id: "sp2", label: "Session mode", detail: "Rotating or sticky" },
  { id: "sp3", label: "IP reputation", detail: "Clean mobile exit" },
  { id: "sp4", label: "Geo-location", detail: "City-level exit" },
];

export function SecurityVisual() {
  const reduced = useReducedMotion();
  const active = useAutoCycle(SECURITY_PROBES.length + 1, 1400);
  // Under reduced motion the cycle holds at 0 — show the finished state instead.
  const progress = reduced ? SECURITY_PROBES.length : active;

  return (
    <div className="mt-5 space-y-3">
      {/* Assessment header */}
      <div className={cn("flex items-center gap-3 rounded-xl p-3.5", FLOAT)}>
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: GREEN_TINT }}
        >
          <Shield size={18} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-foreground text-[13px] font-medium">Security assessment</p>
          <p className="text-muted-foreground text-[11.5px]">Authorized probe · mobile exit</p>
        </div>
        <AnimatePresence mode="wait">
          {progress >= SECURITY_PROBES.length ? (
            <motion.span
              key="passed"
              initial={reduced ? false : { opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.25 }}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium"
              style={{ backgroundColor: GREEN_TINT, color: "var(--brand-green)" }}
            >
              <Check size={11} aria-hidden="true" /> Passed
            </motion.span>
          ) : (
            <motion.span
              key="running"
              initial={reduced ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="text-muted-foreground flex items-center gap-1.5 text-[11px]"
            >
              <span className="relative flex h-1.5 w-1.5">
                {!reduced && (
                  <span
                    className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
                    style={{ backgroundColor: "var(--brand-green)" }}
                  />
                )}
                <span
                  className="relative inline-flex h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: "var(--brand-green)" }}
                />
              </span>
              Scanning
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      {/* Probe checklist — checks tick green as the scan advances */}
      <div className={cn("space-y-2.5 rounded-xl p-3.5", FLOAT)}>
        {SECURITY_PROBES.map((probe, i) => {
          const done = i < progress;
          return (
            <div key={probe.id} className="flex items-center gap-3">
              <span className="relative flex h-4 w-4 shrink-0 items-center justify-center">
                <span
                  className={cn(
                    "absolute inset-0 rounded-full border transition-colors duration-300",
                    done ? "border-transparent" : "border-border"
                  )}
                  style={done ? { backgroundColor: "var(--brand-green)" } : undefined}
                />
                {done && (
                  <Check
                    size={10}
                    className="relative"
                    style={{ color: "var(--background)" }}
                    aria-hidden="true"
                  />
                )}
              </span>
              <span
                className={cn(
                  "flex-1 text-[11.5px] transition-colors duration-300",
                  done ? "text-foreground font-medium" : "text-muted-foreground"
                )}
              >
                {probe.label}
              </span>
              <span
                className={cn(
                  "text-[11px] transition-colors duration-300",
                  done ? "text-brand-green" : "text-muted-foreground/60"
                )}
              >
                {probe.detail}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================ AI AGENTS ============================ */
/* An agent run's request log, watched from the console a developer would have
   open. Autonomous agents and browser-automation jobs die on the first request
   that gets blocked, so the moment worth showing is the opposite: a run that
   keeps answering 200, each task leaving through a different residential exit.
   The cursor works the rotate control — the session control this card's use case
   is actually about — and each rotation dispatches the next task on a fresh IP. */
const AGENT_TASKS = [
  { id: "at1", method: "GET", path: "/catalog?page=2", ip: "45.9.112.8" },
  { id: "at2", method: "POST", path: "/api/session", ip: "88.214.7.31" },
  { id: "at3", method: "GET", path: "/product/1042", ip: "102.44.66.9" },
  { id: "at4", method: "GET", path: "/reviews?sort=new", ip: "77.13.209.4" },
];

/* Click Rotate to send the next task out through a fresh exit, then drift down
   to the exit line to read the address it actually left on. */
const AGENT_CURSOR_SCRIPT: FakeCursorStep[] = [
  { id: "rotate", target: "agent-rotate", hold: 2100 },
  { id: "read-exit", target: "agent-exit", at: 0.32, hold: 950, arrive: "release" },
];

/** AI agents — a live agent request log. The fake cursor clicks Rotate to
    dispatch the next task; the log highlights the request in flight, every row
    answers 200, and the exit IP beneath rotates with it. */
export function AgentsVisual() {
  const reduced = useReducedMotion();
  const [idx, setIdx] = useState(0);
  const current = AGENT_TASKS[idx];

  const handleClick = useCallback((s: FakeCursorStep) => {
    if (s.id === "rotate") setIdx((v) => (v + 1) % AGENT_TASKS.length);
  }, []);

  const { frameRef, setTarget, cursor, clicking } = useFakeCursor({
    script: AGENT_CURSOR_SCRIPT,
    onClick: handleClick,
  });

  return (
    <div ref={frameRef} className="relative mt-5 space-y-3">
      <FakeCursor cursor={cursor} clicking={clicking} />

      {/* Run header — the rotate control is what the cursor works */}
      <div className={cn("flex items-center gap-3 rounded-xl p-3.5", FLOAT)}>
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: GREEN_TINT }}
        >
          <Cpu size={18} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-foreground text-[13px] font-medium">Agent run</p>
          {/* No "done" state — a healthy agent run is simply still going. */}
          <p className="text-muted-foreground flex items-center gap-1.5 text-[11.5px]">
            <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
              {!reduced && (
                <span className="bg-brand-green absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" />
              )}
              <span className="bg-brand-green relative inline-flex h-1.5 w-1.5 rounded-full" />
            </span>
            Running · REST API
          </p>
        </div>
        <span
          ref={setTarget("agent-rotate")}
          className="flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium"
          style={{ backgroundColor: GREEN_TINT, color: "var(--brand-green)" }}
        >
          <RefreshCw size={11} aria-hidden="true" /> Rotate
        </span>
      </div>

      {/* Request log — the highlighted row is the request in flight */}
      <div className={cn("rounded-xl p-3.5", FLOAT)}>
        <div className="space-y-2">
          {AGENT_TASKS.map((task, i) => {
            const on = i === idx;
            return (
              <div key={task.id} className="flex items-center gap-2.5">
                <span
                  className={cn(
                    "w-10 shrink-0 rounded py-0.5 text-center font-mono text-[9.5px] font-semibold transition-colors duration-300",
                    on ? "text-brand-green" : "text-muted-foreground/70"
                  )}
                  style={{
                    backgroundColor: on
                      ? GREEN_TINT
                      : "color-mix(in srgb, var(--foreground) 6%, transparent)",
                  }}
                >
                  {task.method}
                </span>
                <span
                  className={cn(
                    "min-w-0 flex-1 truncate font-mono text-[11px] transition-colors duration-300",
                    on ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  {task.path}
                </span>
                <span
                  className={cn(
                    "shrink-0 font-mono text-[11px] tabular-nums transition-colors duration-300",
                    on ? "text-brand-green font-medium" : "text-muted-foreground/60"
                  )}
                >
                  200
                </span>
              </div>
            );
          })}
        </div>
        {/* One exit line under the log, so the rotation is legible without
            crowding every row with an address. */}
        <div
          ref={setTarget("agent-exit")}
          className="border-border mt-3 flex items-center gap-2 border-t pt-2.5"
        >
          <span className="text-muted-foreground shrink-0 text-[11px]">Exit IP</span>
          <motion.span
            key={current.id}
            {...fadeKey(reduced)}
            className="font-mono text-[11px] font-medium tabular-nums"
            style={{ color: "var(--brand-green)" }}
          >
            {current.ip}
          </motion.span>
          <span className="text-muted-foreground/60 ml-auto shrink-0 text-[11px]">Residential</span>
        </div>
      </div>
    </div>
  );
}

/* ============================ AD VERIFICATION ============================ */
/* The same ad slot as it is actually served in each market. An ad-verification
   team checks that the creative, the landing domain and the local price shown to
   a visitor are the ones that were bought, so the card swaps market and
   re-renders the unit that market really gets. The markets are a segmented
   control rather than a dropdown — the Brand card in this same grid already owns
   the dropdown idiom, and picking a market is a one-tap switch anyway. */
const AD_MARKETS = [
  {
    id: "us",
    market: "US",
    advertiser: "shop.example.com",
    headline: "Summer Sale — 40% off sitewide",
    cta: "Shop now",
    price: "$29.99",
  },
  {
    id: "de",
    market: "DE",
    advertiser: "shop.example.de",
    headline: "Sommerschlussverkauf — 40% Rabatt",
    cta: "Jetzt kaufen",
    price: "27,99 €",
  },
  {
    id: "jp",
    market: "JP",
    advertiser: "shop.example.jp",
    headline: "サマーセール — 全品40%オフ",
    cta: "今すぐ購入",
    price: "¥4,480",
  },
];

/* Tap the next market in the segmented control, then drift onto the creative
   that market was served to check it. */
const AD_CURSOR_SCRIPT: FakeCursorStep[] = [
  { id: "pick-market", target: "ad-market", hold: 2200 },
  { id: "check-creative", target: "ad-creative", at: 0.5, atY: 0.32, hold: 950, arrive: "release" },
];

/** Ad verification — the ad unit a local visitor is served, swapped per market.
    The fake cursor taps the next market in the segmented control; the creative,
    landing domain, CTA and local price all re-render for that geography. */
export function AdVisual() {
  const reduced = useReducedMotion();
  const [idx, setIdx] = useState(0);
  const ad = AD_MARKETS[idx];
  const nextIdx = (idx + 1) % AD_MARKETS.length;

  const handleClick = useCallback((s: FakeCursorStep) => {
    if (s.id === "pick-market") setIdx((v) => (v + 1) % AD_MARKETS.length);
  }, []);

  const { frameRef, setTarget, cursor, clicking } = useFakeCursor({
    script: AD_CURSOR_SCRIPT,
    onClick: handleClick,
  });

  return (
    <div ref={frameRef} className="relative mt-5 space-y-3">
      <FakeCursor cursor={cursor} clicking={clicking} />

      {/* Market segmented control — the cursor taps the next market */}
      <div className="flex items-center gap-2">
        <span className={cn("flex items-center gap-0.5 rounded-lg p-1", FLOAT)}>
          {AD_MARKETS.map((m, i) => {
            const on = i === idx;
            return (
              <span
                key={m.id}
                ref={i === nextIdx ? setTarget("ad-market") : undefined}
                className={cn(
                  "rounded-md px-2.5 py-1 text-[11.5px] font-medium transition-colors duration-300",
                  on ? "text-brand-green" : "text-muted-foreground"
                )}
                style={on ? { backgroundColor: GREEN_TINT } : undefined}
              >
                {m.market}
              </span>
            );
          })}
        </span>
        <span
          className="ml-auto flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium"
          style={{ backgroundColor: GREEN_TINT, color: "var(--brand-green)" }}
        >
          <Check size={11} aria-hidden="true" /> Verified
        </span>
      </div>

      {/* The served ad unit. Fixed height so a longer headline in one market
          never resizes the card as the markets change. */}
      <div
        ref={setTarget("ad-creative")}
        className={cn("flex h-38 flex-col rounded-xl p-3.5", FLOAT)}
      >
        <div className="flex items-center gap-2">
          <span
            className="text-muted-foreground rounded px-1.5 py-0.5 text-[9.5px] font-semibold tracking-wide uppercase"
            style={{ backgroundColor: "color-mix(in srgb, var(--foreground) 6%, transparent)" }}
          >
            Sponsored
          </span>
          <motion.span
            key={`${ad.id}-domain`}
            {...fadeKey(reduced)}
            className="text-muted-foreground min-w-0 flex-1 truncate text-[11px]"
          >
            {ad.advertiser}
          </motion.span>
        </div>

        <motion.p
          key={`${ad.id}-headline`}
          {...fadeKey(reduced)}
          className="text-foreground mt-2.5 line-clamp-2 text-[13px] leading-snug font-medium"
        >
          {ad.headline}
        </motion.p>

        <div className="mt-auto flex items-center gap-2 pt-2.5">
          <motion.span
            key={`${ad.id}-cta`}
            {...fadeKey(reduced)}
            className="rounded-lg px-3 py-1.5 text-[11.5px] font-semibold"
            style={{ backgroundColor: "var(--brand-green)", color: "var(--background)" }}
          >
            {ad.cta}
          </motion.span>
          <motion.span
            key={`${ad.id}-price`}
            {...fadeKey(reduced)}
            className="text-foreground ml-auto text-[13px] font-semibold tabular-nums"
          >
            {ad.price}
          </motion.span>
        </div>
      </div>
    </div>
  );
}

/* ============================ GEOLOCATION TESTING ============================ */
/* What the site under test actually resolves the visitor to. Geolocation testing
   is only useful if the exit is precise, so the card shows the fields a QA team
   checks — country, city, ISP, local time — re-resolving together each time the
   exit changes. City and ISP are the two that prove the targeting went deeper
   than the country, so they carry the accent. */
const GEO_EXITS = [
  {
    id: "de-berlin",
    country: "Germany",
    city: "Berlin",
    isp: "Deutsche Telekom",
    time: "CET · UTC+1",
  },
  {
    id: "jp-osaka",
    country: "Japan",
    city: "Osaka",
    isp: "NTT Communications",
    time: "JST · UTC+9",
  },
  {
    id: "br-sao",
    country: "Brazil",
    city: "São Paulo",
    isp: "Vivo Fibra",
    time: "BRT · UTC−3",
  },
];

const GEO_FIELDS = [
  { id: "country", label: "Country", key: "country" },
  { id: "city", label: "City", key: "city" },
  { id: "isp", label: "ISP", key: "isp" },
  { id: "time", label: "Local time", key: "time" },
] as const;

/* Click Re-check to resolve a new exit, then drift down the resolved fields to
   read them — a refresh control rather than a menu, so this card doesn't repeat
   the Ad card's segmented control or the Brand card's dropdown. */
const GEO_CURSOR_SCRIPT: FakeCursorStep[] = [
  { id: "recheck", target: "geo-recheck", hold: 2200 },
  { id: "read-fields", target: "geo-fields", at: 0.78, atY: 0.55, hold: 950, arrive: "release" },
];

/** Geolocation testing — the exit's resolved location card. The fake cursor
    clicks Re-check; country, city, ISP and local time all re-resolve for the new
    exit, with city and ISP accented as the proof of sub-country targeting. */
export function GeoVisual() {
  const reduced = useReducedMotion();
  const [idx, setIdx] = useState(0);
  const exit = GEO_EXITS[idx];

  const handleClick = useCallback((s: FakeCursorStep) => {
    if (s.id === "recheck") setIdx((v) => (v + 1) % GEO_EXITS.length);
  }, []);

  const { frameRef, setTarget, cursor, clicking } = useFakeCursor({
    script: GEO_CURSOR_SCRIPT,
    onClick: handleClick,
  });

  return (
    <div ref={frameRef} className="relative mt-5 space-y-3">
      <FakeCursor cursor={cursor} clicking={clicking} />

      {/* Resolved-location header — the re-check control is the cursor's target */}
      <div className={cn("flex items-center gap-3 rounded-xl p-3.5", FLOAT)}>
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: GREEN_TINT }}
        >
          <MapPin size={18} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-foreground text-[13px] font-medium">Exit location</p>
          <motion.p
            key={`${exit.id}-sub`}
            {...fadeKey(reduced)}
            className="text-muted-foreground truncate text-[11.5px]"
          >
            {exit.city}, {exit.country}
          </motion.p>
        </div>
        <span
          ref={setTarget("geo-recheck")}
          className="flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium"
          style={{ backgroundColor: GREEN_TINT, color: "var(--brand-green)" }}
        >
          <RefreshCw size={11} aria-hidden="true" /> Re-check
        </span>
      </div>

      {/* Resolved fields — the label column is a fixed width so a longer value in
          one market never shifts the rows as the exits change. */}
      <div ref={setTarget("geo-fields")} className={cn("space-y-2.5 rounded-xl p-3.5", FLOAT)}>
        {GEO_FIELDS.map((field, i) => {
          const precise = field.key === "city" || field.key === "isp";
          return (
            <div key={field.id} className="flex items-center gap-3">
              <span className="text-muted-foreground w-20 shrink-0 text-[11px]">{field.label}</span>
              <motion.span
                key={`${exit.id}-${field.id}`}
                {...fadeKey(reduced, i * 0.06)}
                className={cn(
                  "min-w-0 flex-1 truncate text-right text-[11.5px]",
                  precise ? "font-medium" : "text-foreground"
                )}
                style={precise ? { color: "var(--brand-green)" } : undefined}
              >
                {exit[field.key]}
              </motion.span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
